export class StockfishAdapter {
  constructor(workerPath = 'stockfish11.js') {
    this.workerCandidates = [workerPath, 'stockfish.asm.js', 'stockfish.js', 'stockfish11.js'];
    this.workerIndex = 0;
    this.worker = null;
    this._onBestMove = null;
    this._onInfo = null;
    this._onError = null;
  }

  init() {
    if (this.worker) this.dispose();
    this._initWorker(this.workerCandidates[this.workerIndex]);
  }

  _initWorker(path) {
    try {
      const globalBlobUrl = typeof window !== 'undefined' && window.__STOCKFISH_BLOB_URL__;
      const workerTarget = globalBlobUrl || path;
      this.worker = new Worker(workerTarget);
      this.worker.onmessage = (e) => this._handleMessage(e);
      this.worker.onerror = (e) => this._handleError(e);
      this._post('uci');
      this._post('isready');
      this._post('ucinewgame');
    } catch (e) {
      this._handleError(e);
    }
  }

  _handleError(err) {
    try { this.worker?.terminate(); } catch (_) {}
    this.worker = null;
    if (this.workerIndex + 1 < this.workerCandidates.length) {
      this.workerIndex += 1;
      this._initWorker(this.workerCandidates[this.workerIndex]);
      return;
    }
    if (this._onError) {
      this._onError(err);
    } else {
      // eslint-disable-next-line no-console
      console.error('[StockfishAdapter] worker error', err);
    }
  }

  dispose() {
    try { this.worker?.terminate(); } catch (_) {}
    this.worker = null;
    this._onBestMove = null;
    this._onInfo = null;
    this._onError = null;
    this.workerIndex = 0;
  }

  setPosition(fen) {
    if (!this.worker) return;
    this._post(`position fen ${fen}`);
  }

  goDepth(depth) {
    if (!this.worker) return;
    this._post(`go depth ${depth}`);
  }

  goMovetime(ms) {
    if (!this.worker) return;
    this._post(`go movetime ${ms}`);
  }

  stop() {
    if (!this.worker) return;
    this._post('stop');
  }

  onBestMove(cb) { this._onBestMove = cb; }
  onInfo(cb) { this._onInfo = cb; }
  onError(cb) { this._onError = cb; }

  _post(cmd) {
    try { this.worker?.postMessage(cmd); } catch (e) { this._handleError(e); }
  }

  _handleMessage(message) {
    const line = typeof message?.data === 'string' ? message.data : String(message);
    // Emit search info lines when available (robust to prefixes)
    if (this._onInfo && (/^info\b/.test(line) || /\bscore\s+(cp|mate)\b/.test(line))) {
      const info = this._parseInfo(line);
      if (info) {
        try { this._onInfo(info); } catch (_) {}
      }
    }
    const match = line.match(/^bestmove ([a-h][1-8])([a-h][1-8])([qrbn])?/);
    if (match && this._onBestMove) {
      this._onBestMove({ from: match[1], to: match[2], promotion: match[3] || null });
      return;
    }
    // Optional debug logging for other UCI lines
    if (/^bestmove/.test(line) && !this._onBestMove) {
      try { console.warn('[engine] bestmove with no handler:', line); } catch (_) {}
    }
  }

  _parseInfo(line) {
    // Example lines:
    // info depth 20 seldepth 32 multipv 1 score cp 23 nodes 12345 nps 100000 pv e2e4 ...
    // info depth 15 score mate 3 pv ...
    try {
      const parts = line.trim().split(/\s+/g);
      let depth = null;
      let seldepth = null;
      let multipv = null;
      let scoreType = null; // 'cp' | 'mate'
      let scoreValue = null; // integer
      let pvIndex = parts.indexOf('pv');
      let pv = null;
      for (let i = 1; i < parts.length; i++) {
        const tok = parts[i];
        if (tok === 'depth' && i + 1 < parts.length) {
          depth = parseInt(parts[i + 1], 10);
          i += 1;
          continue;
        }
        if (tok === 'seldepth' && i + 1 < parts.length) {
          seldepth = parseInt(parts[i + 1], 10);
          i += 1;
          continue;
        }
        if (tok === 'multipv' && i + 1 < parts.length) {
          multipv = parseInt(parts[i + 1], 10);
          i += 1;
          continue;
        }
        if (tok === 'score' && i + 2 < parts.length) {
          const t = parts[i + 1];
          const vStr = parts[i + 2];
          if (t === 'cp' || t === 'mate') {
            scoreType = t;
            // Some engines may include bounds: "lowerbound"/"upperbound" after value; we ignore
            const val = parseInt(vStr, 10);
            if (!Number.isNaN(val)) scoreValue = val;
            i += 2;
            continue;
          }
        }
      }
      if (pvIndex >= 0 && pvIndex + 1 < parts.length) {
        pv = parts.slice(pvIndex + 1).join(' ');
      }
      if (scoreType == null || scoreValue == null) return null;
      return {
        depth: depth != null ? depth : undefined,
        seldepth: seldepth != null ? seldepth : undefined,
        multipv: multipv != null ? multipv : undefined,
        score: { type: scoreType, value: scoreValue },
        pv: pv || undefined
      };
    } catch (_) {
      return null;
    }
  }
}


